import React, { useEffect } from 'react';
import { Box } from '@landr/maestro';
import { Languages } from '@landr/core';
import { navigate } from 'gatsby';
import { useFlags } from 'context/FlagsContext';
import { PageContextType, PricingTabsEnum } from 'types';
import {
    addTrailingSlash,
    getDecodedPathname,
    getTwoLetterLanguageCode,
    pricingTabToUid,
    pricingUidToTab,
} from 'helpers';
import { Environment, ScriptLoader } from '@landr/core';
import { useAppContext } from 'context/AppContext';
import { analyticsV2, CtaGoal, CtaType, InteractionSource } from 'analytics';
import { StyledPricingContentBox } from './styled';
import './overrides.scss';

export type PricingSliceProps = {
    pageContext: PageContextType;
    location: Location;
};

const updatePath = (nextTab: PricingTabsEnum, language: Languages, pageContext: PageContextType) => {
    const nextTabInCurrentLanguage = pricingTabToUid[nextTab][language];
    const langPrefix = language !== Languages.English ? `${language}/` : '';
    const path = `${langPrefix}${pageContext.uid}/${nextTabInCurrentLanguage}${window.location.search}`;
    navigate(path);
    return path;
};

const getAnalyticsCtaText = (nextTab: PricingTabsEnum) => {
    switch (nextTab) {
        case PricingTabsEnum.Bundle:
            return 'LANDR Studio';
        case PricingTabsEnum.Mastering:
            return 'Mastering';
        case PricingTabsEnum.Distribution:
            return 'Distribution';
        case PricingTabsEnum.Samples:
            return 'Samples';
        case PricingTabsEnum.Plugins:
            return 'Plugins';
        case PricingTabsEnum.Sessions:
            return 'Sessions';
        case PricingTabsEnum.Courses:
            return 'Education';
        default:
            return nextTab;
    }
};

export const getLocationTabUid = (location: Location): string | undefined => {
    const decodedLocation = addTrailingSlash(getDecodedPathname(location.pathname));
    return Object.keys(pricingUidToTab).find((pricingUid) => decodedLocation.includes(`/${pricingUid}/`));
};

export const PricingTabToHomePageEnum = {
    [PricingTabsEnum.Mastering]: 'Mastering',
    [PricingTabsEnum.Distribution]: 'Distribution',
    [PricingTabsEnum.Sessions]: 'Sessions',
    [PricingTabsEnum.Samples]: 'Samples',
    [PricingTabsEnum.Courses]: 'Education',
    [PricingTabsEnum.Plugins]: 'Plugins',
    [PricingTabsEnum.Bundle]: null,
};

function getSelectedTab(location: Location): PricingTabsEnum {
    getLocationTabUid(location);
    const tabUid = getLocationTabUid(location);
    return tabUid ? pricingUidToTab[tabUid as PricingTabsEnum] : PricingTabsEnum.Bundle;
}

export const Pricing: React.FC<PricingSliceProps> = ({ pageContext, location }) => {
    const language = getTwoLetterLanguageCode(pageContext.lang) as Languages;
    const { setHomePage, resetHomePage } = useAppContext();

    const featureFlags = useFlags();

    const updateHomePageFromTabValue = (nextTab: PricingTabsEnum) => {
        const homePage = PricingTabToHomePageEnum[nextTab];
        if (homePage) {
            setHomePage(homePage);
        } else {
            resetHomePage();
        }
    };
    const handleTabSelected = (nextTab: PricingTabsEnum) => {
        const destinationPath = updatePath(nextTab, language, pageContext);
        updateHomePageFromTabValue(nextTab);

        analyticsV2.trackCTAClicked({
            ctaGoal: CtaGoal.ViewPricing,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.PricingNav,
            ctaText: getAnalyticsCtaText(nextTab),
            ctaDestination: destinationPath,
        });
    };

    useEffect(() => {
        const selectedTab = getSelectedTab(location);

        updateHomePageFromTabValue(selectedTab);
    }, [location]);

    useEffect(() => {
        async function renderPricing() {
            const scriptLoader = new ScriptLoader();

            // script loader will ignore the script if it is already loaded
            await scriptLoader.loadScript(process.env.GATSBY_PRICING_SCRIPT_URL || '');

            const pricingProps = {
                domElementId: 'landr-pricing-container',
                initialSelectedTab: getSelectedTab(location),
                language,
                environment: process.env.GATSBY_ENV as Environment,
                onSelectTab: handleTabSelected,
                locationPathname: location.pathname,
                locationSearch: location.search,
                featureFlags,
            };
            if (window.landrPricing) {
                window.landrPricing.render(pricingProps);
            }
        }

        renderPricing();
    }, [language, location.pathname, location.search, featureFlags]);

    return (
        <StyledPricingContentBox>
            <Box maxWidth="1200px" width="100%">
                <div id="landr-pricing-container" />
            </Box>
        </StyledPricingContentBox>
    );
};
